import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";

/* ============================================================
   SVG LOADING + PARSING
   ============================================================ */

async function fetchText(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Failed to fetch SVG (${r.status})`);
  return await r.text();
}

function extractSvgParts(svgText) {
  // Remove hardcoded width/height so viewBox controls scaling
  const cleaned = svgText
    .replace(/width="[^"]*"/i, "")
    .replace(/height="[^"]*"/i, "");

  const vbMatch = cleaned.match(/viewBox="([^"]+)"/i);
  const viewBox = vbMatch ? vbMatch[1] : `0 0 322.22221 344.44445`;

  const openTagEnd = cleaned.indexOf(">");
  const closeTagStart = cleaned.lastIndexOf("</svg");

  const inner =
    openTagEnd >= 0 && closeTagStart > openTagEnd
      ? cleaned.slice(openTagEnd + 1, closeTagStart)
      : cleaned;

  const nums = viewBox.trim().split(/[\s,]+/).map(Number);
  const vbW = Number.isFinite(nums[2]) ? nums[2] : 322.22221;

  return { viewBox, inner, vbW };
}

/* ============================================================
   SVG UTILITIES
   ============================================================ */

function setDisplay(el, show) {
  if (!el) return;
  el.style.display = show ? "inline" : "none";
}

/**
 * Hide all jaw variants, then show only requested ids.
 */
function applyRender(svgRootEl, showIds) {
  if (!svgRootEl) return;

  const all = svgRootEl.querySelectorAll(
    'g[id^="upper_jaw_"], g[id^="lower_jaw_"]'
  );

  all.forEach((el) => setDisplay(el, false));

  (showIds || []).forEach((id) => {
    const el = svgRootEl.querySelector(`#${CSS.escape(id)}`);
    if (el) setDisplay(el, true);
  });
}

/**
 * Anchor reader (correct version).
 * Uses getCTM() so nested transforms (HEAD_MODEL etc.) are respected.
 */
function readAnchor(svgRootEl, id) {
  const el = svgRootEl?.querySelector(`#${CSS.escape(id)}`);
  if (!el) return null;

  const ctm = el.getCTM?.();
  if (ctm && Number.isFinite(ctm.e) && Number.isFinite(ctm.f)) {
    return { x: ctm.e, y: ctm.f };
  }

  return null;
}

/* ============================================================
   STAGE CONFIGURATION
   ============================================================ */

const ALL_STAGES = ["rest", "closure", "release"];
const ANIM_STAGES = ["closure", "release"]; // animation skips rest

function burstDuration(type) {
  if (type === "strong") return 160;
  if (type === "weak") return 110;
  return 140;
}

function burstStrength(type) {
  if (type === "strong") return 1.0;
  if (type === "weak") return 0.6;
  return 0;
}

/* ============================================================
   COMPONENT
   ============================================================ */

export default function ArticulationSideView({
  baseSvgSrc = `${process.env.REACT_APP_USER_API_URL}/uploads/diagrams/sounds.svg`,
  target,
  size = "normal", // "normal" | "large"
  onOpenModal = null,
  enableModalClick = true,
}) {
  const svgRef = useRef(null);

  const [svgInner, setSvgInner] = useState(null);
  const [stage, setStage] = useState("closure");
  const [animating, setAnimating] = useState(true);

  const [mouthPos, setMouthPos] = useState(null);
  const [glottisPos, setGlottisPos] = useState(null);

  const [flowT, setFlowT] = useState(0);
  const [burstActive, setBurstActive] = useState(false);

  // ✅ NEW: voicing should *not* last the whole "release" stage.
  // We drive it with the same one-shot window as the burst (≈110–160ms).
  const [voiceActive, setVoiceActive] = useState(false);

  const renderMap = target?.render || {};

  /* ------------------------------------------------------------
     Load SVG once
  ------------------------------------------------------------ */
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const txt = await fetchText(baseSvgSrc);
        if (!alive) return;
        setSvgInner(extractSvgParts(txt));
      } catch {
        if (alive) setSvgInner(null);
      }
    })();

    return () => {
      alive = false;
    };
  }, [baseSvgSrc]);

  /* ------------------------------------------------------------
     Apply current stage rendering + read anchors
  ------------------------------------------------------------ */
  useLayoutEffect(() => {
    const root = svgRef.current;
    if (!root) return;

    applyRender(root, renderMap?.[stage]?.show || []);
    setMouthPos(readAnchor(root, "anchor_mouth_exit"));
    setGlottisPos(readAnchor(root, "anchor_glottis"));
  }, [svgInner, stage, renderMap]);

  /* ------------------------------------------------------------
     Animation loop (closure ⇄ release only)
  ------------------------------------------------------------ */
  useEffect(() => {
    if (!animating) return;

    const id = setInterval(() => {
      setStage((s) => {
        const i = ANIM_STAGES.indexOf(s);
        return ANIM_STAGES[(i + 1) % ANIM_STAGES.length];
      });
    }, 650);

    return () => clearInterval(id);
  }, [animating]);

  /* ------------------------------------------------------------
     Manual toggle (stops animation)
  ------------------------------------------------------------ */
  const toggleStage = () => {
    setAnimating(false);
    setStage((s) => ALL_STAGES[(ALL_STAGES.indexOf(s) + 1) % ALL_STAGES.length]);
  };

  /* ------------------------------------------------------------
     One-shot plosive window (burst + voicing)
     - Burst visuals already use a one-shot time window.
     - We make voicing *share the same window* so it is as short as burst.
  ------------------------------------------------------------ */
  const burst = renderMap?.release?.burst || null;
  const wantsVoicingRelease = !!renderMap?.release?.voicing;

  // Final display booleans:
  // - Burst shows only during its one-shot window
  // - Voicing shows only during its one-shot window (same duration as burst)
  const voicing = voiceActive && wantsVoicingRelease;

  useEffect(() => {
    let raf;

    const wantsBurstNow = stage === "release" && !!burst;
    const wantsVoicingNow = stage === "release" && wantsVoicingRelease;

    // If we’re not in release OR nothing to show, reset.
    if (!wantsBurstNow && !wantsVoicingNow) {
      setBurstActive(false);
      setVoiceActive(false);
      setFlowT(0);
      return;
    }

    // Duration: voicing is as short as burst.
    // If a sound requests voicing but has no burst type, use a default.
    const D = burst ? burstDuration(burst) : 140;

    // Start the one-shot window
    setBurstActive(wantsBurstNow);
    setVoiceActive(wantsVoicingNow);
    setFlowT(0);

    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / D);

      // flowT drives the burst curve animation; if no burst, we still tick so voicing ends.
      if (wantsBurstNow) setFlowT(t);

      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        // End window
        setBurstActive(false);
        setVoiceActive(false);
        setFlowT(0);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [stage, burst, wantsVoicingRelease]);

  /* ------------------------------------------------------------
     Scaling
  ------------------------------------------------------------ */
  const S = useMemo(() => {
    const w = svgInner?.vbW || 322.22221;
    return w / 322.22221;
  }, [svgInner?.vbW]);

  const heightClass = size === "large" ? "h-[520px]" : "h-[300px]";

  /* ------------------------------------------------------------
     Airflow geometry
  ------------------------------------------------------------ */
  const flow = useMemo(() => {
    const k = burstStrength(burst);
    const fade = 1 - flowT;

    const baseLen = 105 * S;
    const amp = 10 * S;
    const gap = 8 * S;
    const stroke = 4 * S;
    const opacity = 0.9 * k * fade;

    const phase = flowT * Math.PI * 2;

    const lines = [0, 1, 2].map((i) => {
      const y = i * gap - gap;
      const len = baseLen * (0.8 + 0.2 * i);
      const a = amp * (0.8 + 0.3 * i);
      const p = phase + i * 1.1;

      return {
        d: `M 0 ${y}
            C ${-0.3 * len} ${y + Math.sin(p) * a},
              ${-0.6 * len} ${y + Math.sin(p + 1.4) * a},
              ${-len} ${y + Math.sin(p + 2.6) * a}`,
      };
    });

    return { lines, stroke, opacity };
  }, [S, burst, flowT]);

  /* ------------------------------------------------------------
     Voicing arrow geometry
  ------------------------------------------------------------ */
  const voice = useMemo(
    () => ({
      shaftH: 34 * S,
      shaftW: 5 * S,
      headW: 14 * S,
      headH: 14 * S,
      wiggleDx: 1.5 * S,
      wiggleDy: 2.2 * S,
    }),
    [S]
  );

  /* ============================================================
     RENDER
     ============================================================ */

  return (
    <div>
      {/* Controls only in normal view */}
      {size !== "large" && (
        <div className="mb-2 flex gap-2">
          <button
            onClick={toggleStage}
            className="w-[110px] rounded-xl border px-3 py-2 text-xs font-semibold bg-white hover:bg-slate-50"
          >
            Toggle
          </button>

          <button
            onClick={() => setAnimating((v) => !v)}
            className={`w-[110px] rounded-xl border px-3 py-2 text-xs font-semibold ${
              animating
                ? "bg-sky-600 text-white"
                : "bg-white hover:bg-slate-50"
            }`}
          >
            Animation
          </button>
        </div>
      )}

      <div
        className={`rounded-2xl border bg-white p-4 shadow-sm ${heightClass} ${
          enableModalClick && onOpenModal ? "cursor-pointer" : ""
        }`}
        onClick={() => {
          if (enableModalClick && onOpenModal) onOpenModal();
        }}
      >
        <svg
          ref={svgRef}
          viewBox={svgInner?.viewBox || `0 0 322.22221 344.44445`}
          preserveAspectRatio="xMidYMid meet"
          style={{ width: "100%", height: "100%" }}
        >
          {svgInner && <g dangerouslySetInnerHTML={{ __html: svgInner.inner }} />}

          {/* Burst (one-shot) */}
          {burstActive && mouthPos && (
            <g
              transform={`translate(${mouthPos.x} ${mouthPos.y})`}
              opacity={flow.opacity}
            >
              {flow.lines.map((ln, i) => (
                <path
                  key={i}
                  d={ln.d}
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth={flow.stroke}
                  strokeLinecap="round"
                />
              ))}
            </g>
          )}

          {/* Voicing arrow (one-shot, same duration as burst) */}
          {voicing && glottisPos && (
            <g transform={`translate(${glottisPos.x} ${glottisPos.y})`}>
              <g>
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  values={`0 0; ${voice.wiggleDx} ${-voice.wiggleDy}; 0 0`}
                  dur="0.18s"
                  repeatCount="indefinite"
                />
                <path
                  d={`M 0 0 L 0 ${-voice.shaftH}`}
                  stroke="#2563eb"
                  strokeWidth={voice.shaftW}
                  strokeLinecap="round"
                  fill="none"
                />
                <path
                  d={`M 0 ${-voice.shaftH - voice.headH}
                      L ${-voice.headW / 2} ${-voice.shaftH}
                      L ${voice.headW / 2} ${-voice.shaftH}
                      Z`}
                  fill="#2563eb"
                />
              </g>
            </g>
          )}
        </svg>
      </div>
    </div>
  );
}